import * as fromInvolvedParties from '../actions/involvedParties.actions';
import { Partij } from '@app/core/interfaces/partij';
import { Injector } from '@angular/core';
import { DateService } from 'outshared-lib';
import * as fromClearActions from '../actions/clear.actions';

/**
 *  ids equals correspondentie_volgnummer
 */
export interface State {
    ids: string[];
    entities: { [id: string]: Partij };
    loading: boolean;
    loaded: { [id: string]: boolean };
    errors: any[];
}

const initialState: State = {
    ids: [],
    entities: {},
    loading: false,
    loaded: {},
    errors: [],
};

/**
 *  Object.assign(target, ...sources);
 *
 *  Properties in the target object will be overwritten by properties in the sources if they have the same key.
 *  Later sources' properties will similarly overwrite earlier ones.
 *
 *  Object.assign({}, currentEntities, newEntities);
 */

export function reducer(state = initialState, action: fromInvolvedParties.Actions): State {
    const injector = Injector.create({ providers: [{ provide: DateService, deps: [] }] });
    const dateService: DateService = injector.get(DateService);
    switch (action.type) {
        case fromInvolvedParties.SET_DRIVER:
        case fromInvolvedParties.SET_WITNESS:
        case fromInvolvedParties.SET_CONTACT:
        case fromInvolvedParties.SET_CO_INSURED:
        case fromInvolvedParties.SET_COUNTER_PARTY:
        case fromInvolvedParties.LOAD: {
            return {
                ...state,
                loading: true,
            };
        }

        case fromInvolvedParties.LOAD_SUCCESS: {
            // New entities
            const entity = action.payload.entity;
            const dossier_volgnummer = action.payload.dossier_volgnummer;

            if (!entity) {
                return state;
            }

            // Create new ids
            const ids = state.ids.filter((id) => id !== dossier_volgnummer);

            // Copy new entitie to old entities
            const stateEntity = state.entities[dossier_volgnummer];
            const newEntity = Object.assign({}, stateEntity, entity);

            // Loaded true
            const newLoadedState = { [action.payload.dossier_volgnummer]: true };

            // Return new state
            return {
                ...state,
                ids: [...ids, dossier_volgnummer],
                entities: Object.assign({}, state.entities, {
                    [dossier_volgnummer]: newEntity,
                }),
                loading: false,
                loaded: Object.assign({}, state.loaded, newLoadedState),
            };
        }

        case fromInvolvedParties.UPDATE_BETROKKENE: {
            const type = action.payload.type;
            const betrokkene = action.payload.betrokkene;
            let entity = {
                ...state.entities[action.payload.dossier_volgnummer],
            };

            if (!betrokkene) {
                return state;
            }

            entity = {
                ...entity,
                [betrokkene[type]]: { ...entity[betrokkene[type]] },
            };

            entity.betrokkene[type] = betrokkene;

            return {
                ...state,
                entities: Object.assign({}, state.entities, {
                    [action.payload.dossier_volgnummer]: entity,
                }),
                loading: false,
            };
        }

        case fromInvolvedParties.UPDATE_NATURAL_PERSON: {
            const entity = { ...state.entities[action.payload.dossier_volgnummer] };
            const persoon = { ...action.payload.persoon };
            const rol = action.payload.rol;

            persoon.beeindigd_indicatie = 'N';
            persoon.beeindigd_indicatie_omschrijving = 'Nee';
            persoon.geldigheid_status_code = 'ACTI';
            persoon.ingangsdatum = dateService.nowAsString();
            persoon.registratie_medewerker_id = 0;
            persoon.registratie_organisatie_id = 'INSH';
            persoon.registratie_organisatie_id_omschrijving = 'InShared';
            persoon.soort_np_code = 'NRML';
            persoon.soort_np_omschrijving = 'Normaal';
            persoon.rol = Object.assign({}, rol, {
                tegenpartij: Object.assign({}, null, {
                    bestuurder: null,
                }),
            });

            if (!entity.natuurlijk_persoon) {
                entity.natuurlijk_persoon = [];
            }

            entity.natuurlijk_persoon = Object.assign([], entity.natuurlijk_persoon);

            entity.natuurlijk_persoon.push(persoon);

            return {
                ...state,
                entities: Object.assign({}, state.entities, {
                    [action.payload.dossier_volgnummer]: entity,
                }),
                loading: false,
            };
        }

        case fromInvolvedParties.SET_ERRORS: {
            const dossier_volgnummer = action.payload.dossier_volgnummer;
            const errors = action.payload.errors;

            if (!errors) {
                return state;
            }

            return {
                ...state,
                errors: Object.assign({}, state.errors, {
                    [dossier_volgnummer]: errors,
                }),
                loading: false,
            };
        }

        case fromInvolvedParties.CLEAR_ERRORS: {
            const dossier_volgnummer = action.payload;

            if (!dossier_volgnummer) {
                return state;
            }

            return {
                ...state,
                errors: Object.assign({}, state.errors, {
                    [dossier_volgnummer]: [],
                }),
            };
        }

        case fromInvolvedParties.UPDATE_DRIVER_NATURAL_PERSON: {
            const entity = Object.assign({}, state.entities[action.payload.dossier_volgnummer]);
            const persoon = action.payload.persoon;
            const addressRelationId = action.payload.adres_relatie_id;

            if (!entity.natuurlijk_persoon) {
                entity.natuurlijk_persoon = [];
            }

            const naturalPerson: any[] = entity.natuurlijk_persoon;
            const selectedPersonIndex = naturalPerson.findIndex(
                (person) => person.adres_relatie_id === addressRelationId
            );
            const selectedPerson = naturalPerson[selectedPersonIndex];

            const newEntity = Object.assign({}, entity, {
                ['natuurlijk_persoon']: Object.assign([], naturalPerson, {
                    [selectedPersonIndex]: Object.assign({}, selectedPerson, {
                        rol: Object.assign({}, selectedPerson.rol, {
                            tegenpartij: Object.assign({}, selectedPerson.rol.tegenpartij, {
                                bestuurder: Object.assign({}, selectedPerson.rol.tegenpartij.bestuurder, {
                                    voorletters: persoon.voorletters,
                                    tussenvoegsels: persoon.tussenvoegsels,
                                    achternaam: persoon.achternaam,
                                }),
                            }),
                        }),
                    }),
                }),
            });

            return {
                ...state,
                entities: Object.assign({}, state.entities, {
                    [action.payload.dossier_volgnummer]: newEntity,
                }),
                loading: false,
            };
        }

        case fromInvolvedParties.UPDATE_DRIVER_NOT_NATURAL_PERSON: {
            const entity = Object.assign({}, state.entities[action.payload.dossier_volgnummer]);
            const persoon = action.payload.persoon;
            const addressRelationId = action.payload.adres_relatie_id;

            if (!entity.niet_natuurlijk_persoon) {
                entity.niet_natuurlijk_persoon = [];
            }

            const notNaturalPerson: any[] = entity.niet_natuurlijk_persoon;
            const selectedPersonIndex = notNaturalPerson.findIndex(
                (person) => person.adres_relatie_id === addressRelationId
            );
            const selectedPerson = notNaturalPerson[selectedPersonIndex];

            const newEntity = Object.assign({}, entity, {
                ['niet_natuurlijk_persoon']: Object.assign([], notNaturalPerson, {
                    [selectedPersonIndex]: Object.assign({}, selectedPerson, {
                        rol: Object.assign({}, selectedPerson.rol, {
                            tegenpartij: Object.assign({}, selectedPerson.rol.tegenpartij, {
                                bestuurder: Object.assign({}, selectedPerson.rol.tegenpartij.bestuurder, {
                                    voorletters: persoon.voorletters,
                                    tussenvoegsels: persoon.tussenvoegsels,
                                    achternaam: persoon.achternaam,
                                }),
                            }),
                        }),
                    }),
                }),
            });

            return {
                ...state,
                entities: Object.assign({}, state.entities, {
                    [action.payload.dossier_volgnummer]: newEntity,
                }),
                loading: false,
            };
        }

        case fromClearActions.CLEAR_CLAIMS_STORE: {
            return initialState;
        }

        default: {
            return state;
        }
    }
}

export const getIds = (state: State) => state.ids;
export const getEntities = (state: State) => state.entities;
export const getErrors = (state: State) => state.errors;
export const getLoaded = (state: State) => state.loaded;
export const isLoading = (state: State) => state.loading;
